import { DateRange } from '@softline/ui-core';
import { SalesStatisticsTemplate } from '../types/sales-statistics-template';

/**
 * The cross-filter vocabulary of ai-mcp's statistics tools (`sales_ranking`,
 * `sales_trend`, `sales_compare_periods`, `sales_compare_entities` — see
 * `src/ai_mcp/tools/statistics.py`). The model already speaks these names, so
 * they are also the argument names of the frontend's filter tools; the mapping
 * to `SalesStatisticsTemplate` lives here and nowhere else.
 */
export interface StatisticsFilterArgs {
  date_from?: string;
  date_to?: string;
  customer_id_list?: number[];
  product_id?: string | number;
  product_group_id?: string | number;
  product_main_group_id?: string | number;
  product_sub_group_id?: string | number;
  product_sub_sub_group_id?: string | number;
  /** Clear every cross-filter before applying the rest of the arguments. */
  reset?: boolean;
}

/** Template key ← single-valued ai-mcp argument. */
const SINGLE_VALUE_FIELDS = {
  product_id: 'product',
  product_group_id: 'productGroup',
  product_main_group_id: 'mainGroup',
  product_sub_group_id: 'subGroup',
  product_sub_sub_group_id: 'subSubGroup',
} as const satisfies Record<string, keyof SalesStatisticsTemplate>;

type SingleValueArg = keyof typeof SINGLE_VALUE_FIELDS;

/** Every template key this mapping owns; `reset` nulls all of them. */
const OWNED_FIELDS = [
  'customer',
  ...Object.values(SINGLE_VALUE_FIELDS),
] as const;

/**
 * The customer filter is keyed by `<Kundennummer>,<Subnummer>` (the menu bar's
 * `uniqueField`), while ai-mcp only knows the Kundennummer — main customers
 * (subnumber 0) are the ones the dashboard offers.
 */
const CUSTOMER_MAIN_SUBNUMBER = '0';

function customerFilterValue(id: number): string {
  return `${id},${CUSTOMER_MAIN_SUBNUMBER}`;
}

function customerNumber(value: string): number | null {
  const id = Number(value.split(',')[0]);
  return Number.isFinite(id) ? id : null;
}

export const STATISTICS_FILTER_ARGS_SCHEMA: Record<string, unknown> = {
  type: 'object',
  properties: {
    date_from: { type: 'string', description: 'Startdatum (inklusive), YYYY-MM-DD.' },
    date_to: { type: 'string', description: 'Enddatum (inklusive), YYYY-MM-DD.' },
    customer_id_list: {
      type: 'array',
      items: { type: 'number' },
      description: 'Kundennummern, z.B. [10042].',
    },
    product_id: { type: 'string', description: 'Artikelnummer.' },
    product_group_id: { type: 'string', description: 'Produktgruppen-Code.' },
    product_main_group_id: { type: 'string', description: 'Artikelhauptgruppen-Code.' },
    product_sub_group_id: { type: 'string', description: 'Artikeluntergruppen-Code.' },
    product_sub_sub_group_id: {
      type: 'string',
      description: 'Artikelunteruntergruppen-Code.',
    },
    reset: {
      type: 'boolean',
      description: 'Setzt alle bestehenden Filter zurück, bevor die Argumente angewendet werden.',
    },
  },
};

/**
 * Only the keys actually present are returned. The caller merges them onto the
 * live template — `applyTemplate` nulls every array key it does not receive.
 */
export function statisticsArgsToTemplate(
  args: StatisticsFilterArgs
): Partial<SalesStatisticsTemplate> {
  const template: Partial<SalesStatisticsTemplate> = {};

  if (args.reset) for (const field of OWNED_FIELDS) template[field] = null;

  if (args.date_from && args.date_to)
    template.dateRange = { from: args.date_from, to: args.date_to } as DateRange;

  if (args.customer_id_list?.length)
    template.customer = args.customer_id_list.map(customerFilterValue);

  for (const [arg, field] of Object.entries(SINGLE_VALUE_FIELDS) as [
    SingleValueArg,
    (typeof SINGLE_VALUE_FIELDS)[SingleValueArg],
  ][]) {
    const value = args[arg];
    if (value === undefined || value === null || value === '') continue;
    template[field] = [String(value)];
  }

  return template;
}

/** The inverse, for reporting the visible dashboard filters back to the model. */
export function templateToStatisticsArgs(
  template: SalesStatisticsTemplate
): StatisticsFilterArgs {
  const args: StatisticsFilterArgs = {};

  if (template.dateRange?.from) args.date_from = template.dateRange.from;
  if (template.dateRange?.to) args.date_to = template.dateRange.to;

  const customers = (template.customer ?? [])
    .map(customerNumber)
    .filter((id): id is number => id !== null);
  if (customers.length > 0) args.customer_id_list = customers;

  for (const [arg, field] of Object.entries(SINGLE_VALUE_FIELDS) as [
    SingleValueArg,
    (typeof SINGLE_VALUE_FIELDS)[SingleValueArg],
  ][]) {
    const values = template[field] as string[] | null | undefined;
    if (values?.length) args[arg] = values[0];
  }

  return args;
}
